/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DrawRuleBasis } from '@gh/builder-shared-data';
import {
  code3Formatter,
  createDefaultGridModel,
  ROW_DELETED_CLASS,
} from '@gh/common-internal-shared';
import { getRouteInitData, NumberTypes, TextTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { sliceDataByQuery } from '@gh/core-data';
import { AutocompleteLoadMoreEvent, STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableAutocompleteColumn,
  buildEditableCheckboxColumn,
  buildEditableNumberColumn,
  buildEditableTextColumn,
  FocusedCell,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { includesIgnoreCase } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  DrawRuleCpCc,
  DrawRuleCpStage,
  DrawRuleStage,
  drawRuleStageFieldState,
  getDrawRuleStageId,
  validateDrawRuleStageField,
} from '../../../resources/draw-rule-details.model';

@Component({
  standalone: false,
  selector: 'gh-draw-rule-details-stage-list',
  templateUrl: 'draw-rule-details-stage-list.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawRuleDetailsStageListComponent implements OnInit {
  @Input() editable: boolean;
  @Input() basis: DrawRuleBasis;
  @Input() stages$: Observable<DrawRuleStage[]>;
  @Input() cpStages: DrawRuleCpStage[];
  @Input() cpCostCenters: DrawRuleCpCc[];
  @Input() selectedStageId: ID;
  @Input() focusedCell?: FocusedCell;
  @Input() showDeleted: boolean;

  @Output() add = new EventEmitter<void>();
  @Output() remove = new EventEmitter<DrawRuleStage>();
  @Output() undelete = new EventEmitter<DrawRuleStage>();
  @Output() setField = new EventEmitter<{ stage: DrawRuleStage; name: string; value: any }>();
  @Output() selected = new EventEmitter<DrawRuleStage>();
  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() changeDeleted = new EventEmitter<boolean>();

  gridDefinition: LayoutBasedGridDefinition;

  totalPctDue$: Observable<number>;
  totalJobBudgetPct$: Observable<number>;

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory) {

  }

  ngOnInit(): void {
    const { drawRuleCpStageLayout, drawRuleCcStageLayout } = getRouteInitData(this.route);

    this.totalPctDue$ = this.stages$.pipe(map$((stages) => {
      return stages
        .filter((stage) => !stage.isDepositStage || stage.status === 0)
        .map((stage) => stage.pctDue || 0)
        .reduce((total, pctDue) => total + pctDue, 0);
    }));

    this.totalJobBudgetPct$ = this.stages$.pipe(map$((stages) =>
      stages
        .map((stage) => stage.jobBudgetPct || 0)
        .reduce((total, jobBudgetPct) => total + jobBudgetPct, 0)));

    const model = createDefaultGridModel({
      editable: this.editable,
      getId: getDrawRuleStageId,
      getCellDef: drawRuleStageFieldState,
      validate: validateDrawRuleStageField,
      command: (entity, name, value) => this.setField.emit({ stage: entity, name, value }),
    });
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      layout: {
        [DrawRuleBasis.CriticalPath]: drawRuleCpStageLayout,
        [DrawRuleBasis.CostCenter]: drawRuleCcStageLayout,
      }[this.basis],
      options: {
        rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({ data }: { data: any }) => data.status === 0,
        },
      },
      columns: {
        stageNumber: { valueFormatter: code3Formatter },
        name: buildEditableTextColumn(TextTypes.Text32, model.getCellAccessor('name')),
        isDepositStage: buildEditableCheckboxColumn(model.getCellAccessor('isDepositStage')),
        amountDue: buildEditableNumberColumn(NumberTypes.Price, model.getCellAccessor('amountDue')),
        pctDue: buildEditableNumberColumn(NumberTypes.Percents, model.getCellAccessor('pctDue')),
        isDeductDeposit: buildEditableCheckboxColumn(model.getCellAccessor('isDeductDeposit')),
        jobBudgetPct: buildEditableNumberColumn(NumberTypes.Percents, model.getCellAccessor('jobBudgetPct')),
        cpStage: buildEditableAutocompleteColumn(
          {
            nameProperty: 'name',
            loadMore: ({ load, query }: AutocompleteLoadMoreEvent) => load(sliceDataByQuery(
              query.filter
                ? this.cpStages.filter((stage) => includesIgnoreCase(stage.name, query.filter))
                : this.cpStages,
              query)),
          },
          model.getCellAccessor('cpStage')),
        cpCostCenter: buildEditableAutocompleteColumn(
          {
            nameProperty: 'name',
            loadMore: ({ load, query }: AutocompleteLoadMoreEvent) => load(sliceDataByQuery(
              query.filter
                ? this.cpCostCenters.filter((stage) => includesIgnoreCase(stage.name, query.filter))
                : this.cpCostCenters,
              query)),
          },
          model.getCellAccessor('cpCostCenter')),
      },
    });
  }
}
